import { Chatbot, Modal, Tags, UI } from 'common';
import { useFlags } from 'launchdarkly-react-client-sdk';
import flatten from 'lodash/flatten';
import uniq from 'lodash/uniq';
import React, { useContext } from 'react';
import ChangeCategoryModal from './ChangeCategoryModal.component';
import DeleteProductModal from './DeleteProductModal.component';
import ManageCategoriesModal from './ManageCategoriesModal.component';

const { ChangeTagsModal, ManageTagsModal } = Tags;
const { WrappedToolbar, WrappedButtonType } = UI;
const { ModalContext } = Modal;
const { ChatbotTriggers, launchChatbot } = Chatbot;

const ProductsToolbar = ({
	categoryOptions,
	isAddingNewProduct,
	refreshCategoryOptionsCallback,
	refreshProductsCallback,
	selectedProducts = [],
	tagOptions,
	onClickAddProductCallback,
	onChangeCategoryCallback,
	onChangeSelectedTagsCallback,
	onDeleteProductCallback,
	onUpdateTagOptionsCallback,
}) => {
	const { showModal } = useContext(ModalContext);
	const flags = useFlags();

	const onClickChangeCategoryButton = () => {
		if (selectedProducts.length === 0) {
			return;
		}

		showModal({
			component: ChangeCategoryModal,
			data: {
				categoryOptions,
				selectedProducts,
				label: 'Change Category',
				onChangeCategoryCallback,
			},
		});
	};

	const onClickChangeTagsButton = () => {
		if (selectedProducts.length === 0) {
			return;
		}

		// get all the Tags array in each Product object
		const selectedTags = selectedProducts
			.filter(product => product.Tags.length > 0)
			.map(product => product.Tags);

		showModal({
			component: ChangeTagsModal,
			data: {
				selectedTags: uniq(flatten(selectedTags)), // only pass the unique tag ids
				onChangeSelectedTagsCallback,
			},
		});
	};

	const onClickManageCategoriesButton = () => {
		showModal({
			component: ManageCategoriesModal,
			data: {
				categoryOptions,
				refreshCategoryOptionsCallback,
				refreshProductsCallback,
			},
		});
	};

	const onClickManagaTagsButton = () => {
		showModal({
			component: ManageTagsModal,
			data: {
				tagOptions,
				onUpdateTagOptionsCallback,
			},
		});
	};

	const onClickFeedbackButton = () => {
		launchChatbot({
			flags,
			showModal,
			trigger: ChatbotTriggers.FeedbackButton,
		});
	};

	const onDeleteProductButton = () => {
		showModal({
			component: DeleteProductModal,
			data: {
				onDeleteProductCallback,
				selectCount: selectedProducts.length,
			},
		});
	};

	const buttons = [
		{
			label: 'Add',
			icon: 'add-icon',
			className: 'toolbar-icon-btn',
			dataqa: 'add-new-product',
			disabled: isAddingNewProduct,
			onClick: onClickAddProductCallback,
			tooltip: `<b>Add</b>\nCreate a new product.`,
			tooltipOptions: {
				position: 'bottom',
			},
		},
		{
			label: 'Delete',
			dataqa: 'delete-products',
			disabled: selectedProducts.length === 0,
			onClick: onDeleteProductButton,
			tooltip: '<b>Delete</b>\nDelete selected products.',
			tooltipOptions: {
				position: 'bottom',
			},
		},
		{
			label: 'Categories',
			className: 'categories-toolbar-button',
			buttonType: WrappedButtonType.TertiaryGhost,
			disabled: false,
			model: [
				{
					command: onClickChangeCategoryButton,
					disabled: selectedProducts.length === 0,
					label: 'Change Categories',
				},
				{
					command: onClickManageCategoriesButton,
					label: 'Add/Edit Categories',
				},
			],
			tooltip: '<b>Categories</b>\nManage your product categories.',
			tooltipOptions: {
				position: 'bottom',
			},
		},
		{
			label: 'Tags',
			disabled: false,
			buttonType: WrappedButtonType.TertiaryGhost,
			model: [
				{
					command: onClickChangeTagsButton,
					disabled: selectedProducts.length === 0,
					label: 'Change Tags',
				},
				{
					command: onClickManagaTagsButton,
					label: 'Manage Tags',
				},
			],
			tooltip: '<b>Tags</b>\nManage the tags your products assign to orders.',
			tooltipOptions: {
				position: 'bottom',
			},
		},
		{
			icon: 'feedback-icon',
			buttonType: WrappedButtonType.TertiaryWhite,
			label: 'Feedback',
			dataqa: 'feedback',
			group: 'right',
			onClick: onClickFeedbackButton,
			tooltip: "<b>Feedback</b>\nTell us how we're doing!",
			tooltipOptions: {
				position: 'bottom',
			},
		},
	];

	return <WrappedToolbar buttons={buttons} dataqa="products-toolbar" />;
};

export default ProductsToolbar;
